import React, { useEffect, useState } from 'react';
import { Auth, CognitoUser } from '@aws-amplify/auth';

import { MfaChoiceModal, MfaSmsCodeModal, MfaSmsPhoneModal, MfaTotpSetupModal } from '@/components';
import { useAutoSignInContext, useHandleError } from '@/hooks';
import { ACTION_STATUS, ACTION_TYPES, CHALLENGE_NAMES, PREFERRED_MFA_TYPES } from '@/lib/models';
import { trackAction, toErrorFields } from '@/lib/utils';

interface Props {
	show: boolean;
	onHide(): void;
	user: CognitoUser;
	firstTimeSetup: boolean;
	onComplete(): void;
}

export const MfaFlowModals = ({ show, user, firstTimeSetup, onHide, onComplete }: Props) => {
	const handleError = useHandleError();
	const { emailAddress, password } = useAutoSignInContext();

	const [showMfaChoiceModal, setShowMfaChoiceModal] = useState(false);
	const [showMfaTotpSetupModal, setShowMfaTotpSetupModal] = useState(false);
	const [showMfaSmsPhoneModal, setShowMfaSmsPhoneModal] = useState(false);
	const [showMfaSmsCodeModal, setShowMfaSmsCodeModal] = useState(false);
	const [fauxSignedInUser, setFauxSignedInUser] = useState<CognitoUser>();

	const trackMfaSet = (method: 'SMS' | 'APP') => {
		const idTokenPayload = user.getSignInUserSession?.()?.getIdToken?.()?.payload;
		const identity = {
			emailAddress: user.getUsername?.() ?? idTokenPayload?.email,
			cognitoUserId: idTokenPayload?.sub,
		};

		trackAction({
			actionType: method === 'SMS' ? ACTION_TYPES.MFA_REQUEST_SMS : ACTION_TYPES.MFA_REQUEST_APP,
			status: ACTION_STATUS.SUCCESS,
			...identity,
		});

		if (!firstTimeSetup) {
			trackAction({
				actionType: ACTION_TYPES.MFA_UPDATED,
				status: ACTION_STATUS.SUCCESS,
				...identity,
			});
		}
	};

	const resetFlow = () => {
		setShowMfaChoiceModal(true);
		setShowMfaTotpSetupModal(false);
		setShowMfaSmsPhoneModal(false);
		setShowMfaSmsCodeModal(false);
		setFauxSignedInUser(undefined);
	};

	useEffect(() => {
		if (show) {
			resetFlow();
		} else {
			setShowMfaChoiceModal(false);
			setShowMfaTotpSetupModal(false);
			setShowMfaSmsPhoneModal(false);
			setShowMfaSmsCodeModal(false);
			setFauxSignedInUser(undefined);
		}
	}, [show, user]);

	return (
		<>
			<MfaChoiceModal
				show={showMfaChoiceModal}
				onHide={() => {
					onHide();
				}}
				onTotpClick={() => {
					if (user.challengeName === CHALLENGE_NAMES.SELECT_MFA_TYPE) {
						user.sendMFASelectionAnswer(PREFERRED_MFA_TYPES.SOFTWARE_TOKEN_MFA, {
							onSuccess: (_session) => {
								setShowMfaChoiceModal(false);
								setShowMfaTotpSetupModal(true);
							},
							onFailure: (error) => {
								trackAction({
									actionType: ACTION_TYPES.MFA_REQUEST_APP,
									status: ACTION_STATUS.FAILURE,
									emailAddress: user.getUsername?.(),
									...toErrorFields(error),
								});
								handleError(error);
							},
						});
					} else {
						setShowMfaChoiceModal(false);
						setShowMfaTotpSetupModal(true);
					}
				}}
				onSmsClick={() => {
					if (user.challengeName === CHALLENGE_NAMES.SELECT_MFA_TYPE) {
						user.sendMFASelectionAnswer(PREFERRED_MFA_TYPES.SMS_MFA, {
							onSuccess: (_session) => {
								setShowMfaChoiceModal(false);
								setShowMfaSmsPhoneModal(true);
							},
							onFailure: (error) => {
								trackAction({
									actionType: ACTION_TYPES.MFA_REQUEST_SMS,
									status: ACTION_STATUS.FAILURE,
									emailAddress: user.getUsername?.(),
									...toErrorFields(error),
								});
								handleError(error);
							},
						});
					} else {
						setShowMfaChoiceModal(false);
						setShowMfaSmsPhoneModal(true);
					}
				}}
			/>

			<MfaTotpSetupModal
				user={user}
				show={showMfaTotpSetupModal}
				onBack={() => {
					resetFlow();
				}}
				onHide={() => {
					onHide();
				}}
				onSuccess={() => {
					trackMfaSet('APP');
					onComplete();
				}}
			/>

			<MfaSmsPhoneModal
				username={user.getUsername()}
				firstTimeSetup={firstTimeSetup}
				show={showMfaSmsPhoneModal}
				onBack={() => {
					resetFlow();
				}}
				onHide={() => {
					onHide();
				}}
				onCodeSent={(signedInUser) => {
					setFauxSignedInUser(signedInUser);
					setShowMfaSmsPhoneModal(false);
					setShowMfaSmsCodeModal(true);
				}}
			/>

			<MfaSmsCodeModal
				show={showMfaSmsCodeModal}
				fauxSignedInUser={fauxSignedInUser}
				onBack={() => {
					setShowMfaSmsCodeModal(false);
					setShowMfaSmsPhoneModal(true);
				}}
				onHide={() => {
					onHide();
				}}
				onSuccess={() => {
					trackMfaSet('SMS');
					onComplete();
				}}
				onResendCode={async () => {
					if (firstTimeSetup) {
						const refreshed = await Auth.signIn(emailAddress, password);
						return refreshed;
					}
					const currentUser: CognitoUser = await Auth.currentAuthenticatedUser();
					await Auth.verifyUserAttribute(currentUser, 'phone_number');
					return undefined;
				}}
			/>
		</>
	);
};
